import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { z } from "zod";

import InputError from "@/components/console/common/InputError";
import Checkbox from "@/components/console/form/Checkbox";
import Editor from "@/components/console/form/Editor";
import FileUpload, { FileData } from "@/components/console/form/FileUpload";
import Input from "@/components/console/form/Input";
import { API_URL } from "@/config/apiConfig";
import { useDelBoardFile, useGetBoard, usePostBoardCreate, usePutBoard } from "@/service/console/board";
import { usePopupStore } from "@/store/common/usePopupStore";
import { useAuthStore } from "@/store/console/useAuthStore";

const schema = z.object({
    b_title: z.string().min(1, "제목을 입력해주세요."),
    b_notice: z.string(),
    b_contents: z.string().min(1, "내용을 입력해주세요."),
});

type FormValues = z.infer<typeof schema>;

export default function BoardWrite() {
    const navigate = useNavigate();
    const { category, board_idx } = useParams();
    const { m_email, m_name } = useAuthStore();
    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
        setValue,
        reset,
    } = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            b_title: "",
            b_notice: "0",
            b_contents: "",
        },
    });
    const values = useWatch({ control });
    const [files, setFiles] = useState<FileData[]>([]);
    const [filesData, setFilesData] = useState<File[]>([]);
    const [imgFiles, setImgFiles] = useState<FileData[]>([]);
    const [imgFilesData, setImgFilesData] = useState<File[]>([]);
    const [imgError, setImgError] = useState(false);
    const {
        data: configData,
        isLoading: isInitialLoading,
        refetch,
    } = useGetBoard(category || "", board_idx || "", {
        enabled: Boolean(board_idx),
    });
    const delBoardFileMutation = useDelBoardFile();
    const postBoardCreateMutation = usePostBoardCreate();
    const putBoardMutation = usePutBoard();
    const { setConfirmPop, setLoadingPop } = usePopupStore();

    // 데이터 로딩 또는 저장,수정 중일 때 로딩 팝업 표시
    useEffect(() => {
        const isLoading = isInitialLoading || putBoardMutation.isPending || postBoardCreateMutation.isPending;
        setLoadingPop(isLoading, true);
        return () => setLoadingPop(false);
    }, [isInitialLoading, putBoardMutation.isPending, postBoardCreateMutation.isPending, setLoadingPop]);

    // 게시글 상세 조회
    useEffect(() => {
        if (configData) {
            reset({
                b_title: configData.data.b_title,
                b_notice: configData.data.b_notice,
                b_contents: configData.data.b_contents,
            });
            setFiles(configData.data.b_file);
            if (configData.data.b_img) {
                setImgFiles([
                    { idx: uuidv4(), original_name: configData.data.b_img, url: `${API_URL}/${configData.data.b_img}` },
                ]);
            }
        }
    }, [configData, reset]);

    // 첨부파일 삭제
    const handleConfirmDeleteFile = (idx: number, file_idx: number | string, img?: boolean) => {
        if (board_idx && typeof file_idx === "number") {
            setConfirmPop(true, `해당 파일을 영구 삭제하시겠습니까? <br/>삭제 후에는 복구할 수 없습니다.`, 2, () =>
                handleDeleteFile(file_idx),
            );
        } else {
            if (img) {
                const newList = [...imgFiles];
                newList.splice(idx, 1);
                setImgFiles(newList);
                const newFileData = [...imgFilesData];
                newFileData.splice(idx, 1);
                setImgFilesData(newFileData);
            } else {
                const newList = [...files];
                newList.splice(idx, 1);
                setFiles(newList);
                const newFileData = [...filesData];
                newFileData.splice(idx, 1);
                setFilesData(newFileData);
            }
        }
    };

    // 첨부파일 영구삭제
    const handleDeleteFile = (file_idx: number) => {
        const body = { idx: [file_idx] };
        delBoardFileMutation.mutate(body, {
            onSuccess: () => {
                setConfirmPop(true, "삭제되었습니다.", 1);
                refetch();
            },
        });
    };

    // 저장 확인
    const handleConfirmSave = (data: FormValues) => {
        if (category === "170" && imgFiles.length === 0) {
            setImgError(true);
            return;
        }
        setImgError(false);
        setConfirmPop(true, "저장하시겠습니까?", 2, () => onSubmit(data));
    };

    // 저장하기
    const onSubmit = (data: FormValues) => {
        if (!category) return;

        const { ...restData } = data;
        const baseBody = {
            ...restData,
            category,
            m_email,
            m_name,
            b_file: filesData.length > 0 ? filesData : [],
            m_pwd: "", //고정값
            b_depth: 0, //고정값
            b_secret: "", //고정값
            ...(category === "170" && imgFilesData.length > 0 && { b_img: imgFilesData[0] }),
        };

        // 게시글 수정
        if (board_idx) {
            const body = {
                ...baseBody,
                idx: board_idx,
            };
            putBoardMutation.mutate(body, {
                onSuccess: () => {
                    setConfirmPop(true, "수정되었습니다.", 1);
                    navigate(-1);
                },
            });
        }
        // 게시글 등록
        else {
            postBoardCreateMutation.mutate(baseBody, {
                onSuccess: () => {
                    setConfirmPop(true, "등록되었습니다.", 1);
                    navigate(-1);
                },
            });
        }
    };

    return (
        <div className="bg-white p-[36px_40px] rounded-[8px] shadow-[0_0_8px_0_rgba(0,0,0,0.05)]">
            <div className="py-[10px] text-[24px] font-[600]">
                {category === "166"
                    ? "공지사항"
                    : category === "167"
                      ? "FAQ"
                      : category === "170"
                        ? "인스타그램"
                        : "관련자료"}
            </div>
            <form onSubmit={handleSubmit(handleConfirmSave)}>
                <ul className="mt-[20px] py-[20px] border-t border-[#222] flex flex-col gap-[30px]">
                    <li className="flex">
                        <p className="w-[140px] font-[500] pt-[6px]">제목</p>
                        <div className="flex-1">
                            <Input {...register("b_title")} className="w-full" />
                            <InputError message={errors.b_title?.message} />
                        </div>
                    </li>
                    <li className="flex items-center">
                        <p className="w-[140px] font-[500]">공지 설정</p>
                        <Controller
                            name="b_notice"
                            control={control}
                            render={({ field }) => (
                                <Checkbox
                                    {...field}
                                    checked={field.value === "1"}
                                    txt="체크 시 목록 최상단 노출"
                                    onChange={e => {
                                        const check = e.currentTarget.checked;
                                        if (check) {
                                            setValue("b_notice", "1");
                                        } else {
                                            setValue("b_notice", "0");
                                        }
                                    }}
                                />
                            )}
                        />
                    </li>
                    {category === "170" && ( // 인스타그램 게시글일때만 미리보기 등록 노출
                        <li className="flex">
                            <p className="w-[140px] font-[500] pt-[8px]">미리보기 등록</p>
                            <div className="flex-1">
                                <FileUpload
                                    uploadFiles={imgFiles}
                                    setFiles={setImgFiles}
                                    setFilesData={setImgFilesData}
                                    handleDelt={(idx, file_idx) => handleConfirmDeleteFile(idx, file_idx, true)}
                                    showPreview
                                />
                                {imgError && <InputError message="미리보기 이미지를 등록해주세요." />}
                            </div>
                        </li>
                    )}
                    <li>
                        <Editor value={values.b_contents || ""} onChange={cont => setValue("b_contents", cont)} />
                        <InputError message={errors.b_contents?.message} />
                    </li>
                    <li className="flex">
                        <p className="w-[140px] font-[500] pt-[8px]">파일 첨부</p>
                        <FileUpload
                            uploadFiles={files}
                            setFiles={setFiles}
                            setFilesData={setFilesData}
                            boxClassName="flex-1"
                            maxLength={10}
                            handleDelt={(idx, file_idx) => handleConfirmDeleteFile(idx, file_idx)}
                        />
                    </li>
                </ul>
                <div className="mt-[24px] flex justify-end gap-[10px]">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="w-[240px] h-[50px] text-[18px] text-[#666] font-[500] border border-[#ddd] rounded-[5px]"
                    >
                        취소
                    </button>
                    <button
                        type="submit"
                        className="w-[240px] h-[50px] text-[18px] text-white font-[500] border border-[#ddd] rounded-[5px] bg-[#192030]"
                    >
                        저장
                    </button>
                </div>
            </form>
        </div>
    );
}
